import { useAtomValue, useSetAtom } from "jotai";
import { Cylinder } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import Modal from "@/components/Modal";
import { useToast } from "@/components/Toast";
import Button from "@/components/ui/Button";
import {
  projectByIdAtom,
  projectTanksByProjectIdAtom,
  refetchDataAtom,
  tanksAtom,
  waterPumpVirtualDeviceConfigMapAtom,
} from "@/store/data";
import { virtualDeviceConfigurationAtom } from "@/store/data";
import { createProjectTankAtom, createVirtualDeviceConfigurationAtom, deleteProjectTankAtom } from "@/store/crud";
import { filterAvailableOutputs } from "@/utils/device";
import { resolveFertigationDevice } from "@/utils/fertigation-device";
import { getDeviceModelCommercialName } from "@/utils/device-model";
import { isMeshDevice } from "@/utils/mesh-device-utils";
import { propertyDevicesAtom } from "@/store/data";

/**
 * Per-tank configuration captured in the assign dialog.
 *
 * NOTE: the controller (PumpLink device) is NOT per-tank — it is a single shared
 * choice for the whole dialog, enforced by the single-device fertigation rule
 * (see resolveFertigationDevice). Each tank only picks its own output index on
 * that shared device, plus its project-specific flow rate.
 */
interface TankControllerConfig {
  turnOnOutputIndex: number | null;
  flowRateLh: string;
}

interface AssignTankDialogProps {
  projectId: string;
  isOpen: boolean;
  onClose: () => void;
}

function AssignTankDialog({ projectId, isOpen, onClose }: AssignTankDialogProps) {
  const { showError, showWarning } = useToast();

  const allTanks = useAtomValue(tanksAtom);
  const getProjectTanks = useAtomValue(projectTanksByProjectIdAtom);
  const getProjectById = useAtomValue(projectByIdAtom);
  const propertyDevices = useAtomValue(propertyDevicesAtom);
  const virtualDeviceConfigs = useAtomValue(virtualDeviceConfigurationAtom);
  const waterPumpVdcMap = useAtomValue(waterPumpVirtualDeviceConfigMapAtom);
  const createProjectTank = useSetAtom(createProjectTankAtom);
  const createVirtualDeviceConfiguration = useSetAtom(createVirtualDeviceConfigurationAtom);
  const deleteProjectTank = useSetAtom(deleteProjectTankAtom);
  const refetchData = useSetAtom(refetchDataAtom);

  const [selectedTankIds, setSelectedTankIds] = useState<Set<string>>(new Set());
  const [tankConfigs, setTankConfigs] = useState<Map<string, TankControllerConfig>>(new Map());
  const [assigning, setAssigning] = useState(false);

  const projectTanks = useMemo(() => getProjectTanks(projectId), [getProjectTanks, projectId]);
  const activeProjectTankIds = useMemo(
    () => new Set(projectTanks.filter((pt) => !pt.end_date).map((pt) => pt.tank.id)),
    [projectTanks]
  );

  const availableTanks = useMemo(
    () => allTanks.filter((t) => !activeProjectTankIds.has(t.id)),
    [allTanks, activeProjectTankIds]
  );

  const project = useMemo(() => getProjectById(projectId), [getProjectById, projectId]);

  // Single-device fertigation rule resolution.
  // - Case A (dedicated fertigation pump): locked to the pump's PumpLink device.
  // - Case B (per-tank pumps): locked to the device shared by existing active tank
  //   VDCs; null when no tank is assigned yet (first-tank-wins).
  const resolved = useMemo(() => resolveFertigationDevice(project, waterPumpVdcMap), [project, waterPumpVdcMap]);

  const lockedControllerId = resolved.deviceId;

  // Shared controller for all tanks in this dialog session.
  const [sharedControllerId, setSharedControllerId] = useState<string | null>(lockedControllerId);

  const allMeshControllers = useMemo(
    () => propertyDevices.map((pd) => pd.device).filter((d) => isMeshDevice(d.model)),
    [propertyDevices]
  );

  // Controllers available to pick. When locked (Case A or Case B with existing
  // tanks), only the locked device is selectable. Otherwise (Case B, no tanks
  // yet) any mesh device is allowed and the first pick locks the rest.
  const selectableControllers = useMemo(() => {
    if (lockedControllerId) {
      return allMeshControllers.filter((d) => d.id === lockedControllerId);
    }
    return allMeshControllers;
  }, [allMeshControllers, lockedControllerId]);

  const availableOutputs = useMemo(
    () => filterAvailableOutputs(allMeshControllers, virtualDeviceConfigs),
    [allMeshControllers, virtualDeviceConfigs]
  );

  // Outputs already chosen by sibling tanks in this session (a tank output on
  // the shared device cannot be reused by another tank).
  const outputsTakenInSession = useMemo(() => {
    const taken = new Set<number>();
    tankConfigs.forEach((cfg) => {
      if (cfg.turnOnOutputIndex != null) taken.add(cfg.turnOnOutputIndex);
    });
    return taken;
  }, [tankConfigs]);

  // Keep sharedControllerId in sync with the lock whenever the project/resolution
  // changes (e.g. dialog reopened, or pump assignment changed elsewhere).
  useEffect(() => {
    setSharedControllerId(lockedControllerId);
  }, [lockedControllerId]);

  // Warn once when the project's existing tank VDCs are split across devices
  // (should not happen under the rule, but may exist historically).
  useEffect(() => {
    if (isOpen && resolved.existingTankDeviceIds.length > 1) {
      showWarning({
        message:
          "Os tanques deste projeto estão em mais de um controlador. Todos os tanques devem usar o mesmo PumpLink — reatribua os tanques para uniformizar.",
      });
    }
  }, [isOpen, resolved.existingTankDeviceIds.length, showWarning]);

  const handleToggleTank = (tankId: string) => {
    setSelectedTankIds((prev) => {
      const next = new Set(prev);
      if (next.has(tankId)) {
        next.delete(tankId);
        setTankConfigs((configs) => {
          const nextConfigs = new Map(configs);
          nextConfigs.delete(tankId);
          return nextConfigs;
        });
      } else {
        next.add(tankId);
      }
      return next;
    });
  };

  const handleSharedControllerChange = (controllerId: string) => {
    // Only meaningful when not locked (Case B, first-tank-wins).
    setSharedControllerId(controllerId || null);
    // Reset per-tank output picks — outputs are device-specific.
    setTankConfigs((prev) => {
      const next = new Map<string, TankControllerConfig>();
      prev.forEach((cfg, tankId) => {
        next.set(tankId, { ...cfg, turnOnOutputIndex: null });
      });
      return next;
    });
  };

  const handleOutputChange = (tankId: string, outputIndex: number) => {
    setTankConfigs((prev) => {
      const next = new Map(prev);
      const current = next.get(tankId) || { turnOnOutputIndex: null, flowRateLh: "" };
      next.set(tankId, { ...current, turnOnOutputIndex: outputIndex });
      return next;
    });
  };

  const handleAssign = async () => {
    if (selectedTankIds.size === 0) return;

    // Case A sanity: a dedicated fertigation pump must resolve to a wired device.
    if (resolved.case === "A" && !sharedControllerId) {
      showError({
        message:
          "Não foi possível identificar o controlador da bomba de fertirrigação. Configure a bomba de fertirrigação do projeto antes de atribuir tanques.",
      });
      return;
    }

    // Every selected tank must have a controller + output.
    for (const tankId of selectedTankIds) {
      const cfg = tankConfigs.get(tankId);
      if (!sharedControllerId || !cfg || cfg.turnOnOutputIndex == null) {
        showError({
          message: "Selecione o controlador e a saída de ativação para todos os tanques selecionados.",
        });
        return;
      }
    }

    setAssigning(true);
    try {
      for (const tankId of selectedTankIds) {
        const config = tankConfigs.get(tankId);
        const flowRate = config?.flowRateLh ? parseFloat(config.flowRateLh) : 0;

        const projectTankResult = await createProjectTank({
          project: projectId,
          tank: tankId,
          flow_rate_lh: isNaN(flowRate) ? 0 : flowRate,
          start_date: new Date().toISOString(),
        });

        if (!projectTankResult.success || !projectTankResult.data?.id) return;

        const vdcResult = await createVirtualDeviceConfiguration({
          device: sharedControllerId,
          mode: "continuous",
          turn_on_physical_output_index: config!.turnOnOutputIndex!,
          equipment_type: "tank",
          project_tank: projectTankResult.data.id,
          start_date: new Date().toISOString(),
        } as any);

        if (!vdcResult.success) {
          await deleteProjectTank(projectTankResult.data.id);
          return;
        }
      }

      await refetchData();
      onClose();
      setSelectedTankIds(new Set());
      setTankConfigs(new Map());
    } finally {
      setAssigning(false);
    }
  };

  const handleClose = () => {
    if (!assigning) {
      onClose();
    }
  };

  const controllerLocked = !!lockedControllerId;
  const activeControllerLabel = (() => {
    const dev = allMeshControllers.find((d) => d.id === sharedControllerId);
    if (!dev) return "";
    return dev.metadata?.label ? `${dev.metadata.label} - ${dev.identifier}` : dev.identifier;
  })();

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Atribuir Tanques ao Projeto" size="xl">
      <div className="space-y-6">
        {availableTanks.length === 0 ? (
          <p className="text-neutral-600 text-center py-8">
            Todos os tanques da propriedade já estão atribuídos a este projeto.
          </p>
        ) : (
          <>
            <div>
              <h4 className="text-sm font-medium text-neutral-700 mb-3">Selecione os tanques para atribuir:</h4>
              <div className="space-y-2">
                {availableTanks.map((tank) => (
                  <div key={tank.id}>
                    <label className="flex items-center gap-3 p-3 rounded-lg border border-neutral-200 cursor-pointer hover:bg-neutral-50 transition-colors">
                      <input
                        type="checkbox"
                        checked={selectedTankIds.has(tank.id)}
                        onChange={() => handleToggleTank(tank.id)}
                        className="w-4 h-4 text-blue-600 rounded border-neutral-300 focus:ring-blue-500"
                      />
                      <Cylinder className="w-4 h-4 text-blue-500 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium text-neutral-900">{tank.label}</span>
                        <span className="text-sm text-neutral-500 ml-2">— {tank.nutrient.name}</span>
                        <span className="text-xs text-neutral-400 ml-2">
                          {tank.volume_liters}L · {tank.concentration_g_l}g/L
                        </span>
                      </div>
                    </label>

                    {selectedTankIds.has(tank.id) && (
                      <div className="ml-8 mt-2 p-3 bg-neutral-50 rounded-lg border border-neutral-100 space-y-3">
                        {/* Shared controller — rendered inline on the first selected tank only */}
                        {tank.id === [...selectedTankIds][0] && (
                          <div>
                            <label className="block text-xs font-medium text-neutral-600 mb-1">
                              Controlador (PumpLink) — único para todos os tanques
                            </label>
                            <select
                              value={sharedControllerId ?? ""}
                              onChange={(e) => handleSharedControllerChange(e.target.value)}
                              disabled={controllerLocked}
                              className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none bg-white disabled:bg-neutral-100 disabled:text-neutral-500"
                            >
                              <option value="">
                                {controllerLocked
                                  ? activeControllerLabel || "Selecione o controlador"
                                  : "Selecione o controlador"}
                              </option>
                              {selectableControllers.map((device) => (
                                <option key={device.id} value={device.id}>
                                  {device.metadata?.label
                                    ? `${device.metadata.label} - ${device.identifier}`
                                    : device.identifier}{" "}
                                  - {getDeviceModelCommercialName(device.model)}
                                </option>
                              ))}
                            </select>
                            <p className="text-xs text-neutral-400 mt-1">
                              {resolved.case === "A"
                                ? "Definido pela bomba de fertirrigação do projeto."
                                : controllerLocked
                                  ? "Definido pelo primeiro tanque do projeto."
                                  : "O primeiro controlador selecionado será usado por todos os tanques."}
                            </p>
                          </div>
                        )}

                        <div>
                          <label className="block text-xs font-medium text-neutral-600 mb-1">Saída de Ativação</label>
                          <select
                            value={tankConfigs.get(tank.id)?.turnOnOutputIndex ?? ""}
                            onChange={(e) => handleOutputChange(tank.id, Number(e.target.value))}
                            disabled={!sharedControllerId}
                            className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none bg-white disabled:bg-neutral-100 disabled:text-neutral-500"
                          >
                            <option value="">Selecione a saída</option>
                            {availableOutputs
                              .find((ao) => ao.device.id === sharedControllerId)
                              ?.outputs.filter(
                                (output) =>
                                  !outputsTakenInSession.has(output) ||
                                  output === tankConfigs.get(tank.id)?.turnOnOutputIndex
                              )
                              .map((output) => (
                                <option key={output} value={output} disabled={output > 6}>
                                  {output}
                                  {output > 6 ? " (máx. 6)" : ""}
                                </option>
                              ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-neutral-600 mb-1">Vazão (L/h)</label>
                          <input
                            type="number"
                            min="0"
                            step="0.1"
                            value={tankConfigs.get(tank.id)?.flowRateLh ?? ""}
                            onChange={(e) => {
                              setTankConfigs((prev) => {
                                const next = new Map(prev);
                                const current = next.get(tank.id) || { turnOnOutputIndex: null, flowRateLh: "" };
                                next.set(tank.id, { ...current, flowRateLh: e.target.value });
                                return next;
                              });
                            }}
                            placeholder="0"
                            className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none bg-white"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-4 pt-4 border-t border-neutral-200">
              <Button
                onClick={handleAssign}
                loading={assigning}
                disabled={selectedTankIds.size === 0}
                variant="primary"
                className="flex-1"
              >
                Atribuir {selectedTankIds.size > 0 ? `(${selectedTankIds.size})` : ""}
              </Button>
              <Button onClick={handleClose} disabled={assigning} variant="secondary" className="flex-1">
                Cancelar
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

export default AssignTankDialog;
